import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExceptionType, ScheduleBlock } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import { ExceptionFormModal } from './ExceptionFormModal';
import { WeeklyScheduleEditor } from './WeeklyScheduleEditor';
import {
  useScheduleExceptionDelete,
  useScheduleExceptions,
  useUserDirectory,
  useUserSchedule,
  useUserScheduleSave,
} from './api';

const EXCEPTION_BADGE_CLASS: Record<ExceptionType, string> = {
  VACATION: 'bg-amber-50 text-amber-700 ring-amber-200',
  SICK_LEAVE: 'bg-rose-50 text-rose-700 ring-rose-200',
  TIME_OFF: 'bg-zinc-100 text-zinc-700 ring-zinc-300',
  REPLACE: 'bg-sky-50 text-sky-700 ring-sky-200',
  EXTRA_DAY: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

export function SchedulesPage(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isEmployee = user?.role === 'EMPLOYEE';
  const canMutate = user?.role === 'ADMIN' || user?.role === 'RECEPTION';

  const directory = useUserDirectory();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Pracownik widzi tylko siebie — wybór z dropdownu nie ma sensu.
  // Dla ADMIN/RECEPTION: po załadowaniu directory wybierz pierwszego pracownika
  // jeśli nikt nie jest jeszcze wybrany.
  useEffect(() => {
    if (isEmployee && user) {
      setSelectedId(user.id);
      return;
    }
    if (!selectedId && directory.data && directory.data.length > 0) {
      setSelectedId(directory.data[0].id);
    }
  }, [isEmployee, user, directory.data, selectedId]);

  const schedule = useUserSchedule(selectedId);
  const saveSchedule = useUserScheduleSave(selectedId ?? 0);
  const exceptions = useScheduleExceptions(selectedId);
  const removeException = useScheduleExceptionDelete(selectedId ?? 0);

  const onSaveSchedule = async (blocks: ScheduleBlock[]) => {
    setActionError(null);
    await saveSchedule.mutateAsync({ blocks });
  };

  const onRemoveException = async (id: number) => {
    setActionError(null);
    try {
      await removeException.mutateAsync(id);
    } catch (err) {
      setActionError(extractApiError(err));
    }
  };

  const selectedEmployee = useMemo(
    () => directory.data?.find((e) => e.id === selectedId) ?? null,
    [directory.data, selectedId],
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-900">{t('schedules.title')}</h1>
        <p className="mt-1 text-sm text-zinc-500">{t('schedules.subtitle')}</p>
      </header>

      {!isEmployee && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
          <label className="text-sm text-zinc-600" htmlFor="employee-select">
            {t('schedules.employee')}:
          </label>
          <select
            id="employee-select"
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(Number(e.target.value) || null)}
            className="input min-w-[16rem]"
            disabled={directory.isLoading}
          >
            {(directory.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.lastName} {u.firstName} · {t(`roles.${u.role}` as const)}
              </option>
            ))}
          </select>
          {selectedEmployee?.color && (
            <span
              className="inline-block h-4 w-4 rounded-full ring-1 ring-zinc-300"
              style={{ background: selectedEmployee.color }}
              aria-hidden
            />
          )}
        </div>
      )}

      {actionError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {actionError}
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            {t('schedules.weekly.title')}
          </h2>
          {schedule.isFetching && !schedule.isLoading && (
            <span className="text-xs text-zinc-400">{t('common.loading')}</span>
          )}
        </div>
        {schedule.isLoading ? (
          <div className="rounded-xl bg-white p-6 text-center text-sm text-zinc-400 ring-1 ring-zinc-200">
            {t('common.loading')}
          </div>
        ) : schedule.error ? (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {extractApiError(schedule.error)}
          </div>
        ) : (
          <WeeklyScheduleEditor
            schedule={schedule.data}
            readOnly={!canMutate}
            saving={saveSchedule.isPending}
            saveError={saveSchedule.error}
            onSave={onSaveSchedule}
          />
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            {t('schedules.exceptions.title')}
          </h2>
          {canMutate && selectedId !== null && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setExceptionOpen(true)}
            >
              {t('schedules.exceptions.add')}
            </button>
          )}
        </div>
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-zinc-200">
          <table className="min-w-full divide-y divide-zinc-200">
            <thead className="bg-zinc-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3">{t('schedules.exception.date')}</th>
                <th className="px-4 py-3">{t('schedules.exception.type')}</th>
                <th className="px-4 py-3">{t('schedules.exception.hours')}</th>
                <th className="px-4 py-3">{t('schedules.exception.reason')}</th>
                {canMutate && <th className="w-24 px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 text-sm">
              {exceptions.isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-zinc-400">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : (exceptions.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-zinc-400">
                    {t('schedules.exceptions.empty')}
                  </td>
                </tr>
              ) : (
                (exceptions.data ?? []).map((ex) => (
                  <tr key={ex.id}>
                    <td className="px-4 py-3 font-mono text-zinc-700">{ex.date}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs ring-1 ${EXCEPTION_BADGE_CLASS[ex.type]}`}
                      >
                        {t(`schedules.exception.types.${ex.type}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">
                      {ex.startTime && ex.endTime
                        ? `${ex.startTime}–${ex.endTime}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{ex.reason ?? '—'}</td>
                    {canMutate && (
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => void onRemoveException(ex.id)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          {t('schedules.exceptions.remove')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedId !== null && (
        <ExceptionFormModal
          userId={selectedId}
          open={exceptionOpen}
          onClose={() => setExceptionOpen(false)}
        />
      )}
    </div>
  );
}
